import Vue from 'vue';
import setupRouter, { Routes } from './plugins/router';
import setupTracking from './plugins/tracking';
import App from './App.vue';
import store from './store';
import './styles/index.scss';
import i18n from './i18n';
import vuetify from './plugins/vuetify';
import TokenService from './services/TokenService';
import Criteria from '@muz/jobboard-vue-client/src/store/types/Criteria';
import { JobAdSuggestionsService } from '@muz/jobboard-vue-client/src/services/JobAdSuggestionsService';
import config from './config';
import tinymcePlugin from './plugins/tinymce';
import PushPullAnalysisService from '@/services/PushPullAnalysisService';
import AiWriterService from '@/services/AiWriterService';

if (process.env.VUE_APP_DEPLOYMENT_TYPE === 'staging') {
    Vue.config.devtools = true;
}

/** This is a Vue bug (debatable, i consider it a bug) with SVG icons.
 The console get's spammed with warnings that have no consequence in our use case.
 This disables them.*/
const ignoreWarnMessage =
    'The .native modifier for v-on is only valid on components but it was used on <svg>.';
/* eslint-disable no-unused-vars */
Vue.config.warnHandler = function (msg, vm, trace) {
    // `trace` is the component hierarchy trace
    if (msg === ignoreWarnMessage) {
        msg = null;
        vm = null;
        trace = null;
    }
};
/* eslint-enable no-unused-vars */

const router = setupRouter(config);

setupTracking(router);

let loggedIn = false;
let loginStateChanged = false;

router.beforeEach(async (to, from, next) => {
    // Check if user passed an offline / refresh token as url param
    if (to.query.token) {
        // Use the refresh token to log in the user
        await TokenService.refresh({
            refreshToken: to.query.token,
        });

        // Remove the token from the url and redirect to the requested page
        delete to.query.token;
        return next(to);
    }

    // Check if user is beesite user
    const isBeesiteUser = TokenService.isBeesiteUser();

    // beesite user should only be able to access the beesite job ad analysis + optimization and the imprint and privacy policy
    if (
        isBeesiteUser &&
        !to.path.includes('job-ad-analysis-beesite') &&
        to.name !== Routes.IMPRINT &&
        to.name !== Routes.PRIVACY_POLICY
    ) {
        return next({ path: '/error' });
    }

    const requiresAuth = to.meta.requiresAuth;
    loginStateChanged = loggedIn !== TokenService.isAuthenticated();
    loggedIn = TokenService.isAuthenticated();

    if (loginStateChanged) {
        if (loggedIn) {
            window._paq.push(['setUserId', TokenService.getEmail()]);
        } else {
            window._paq.push(['resetUserId']);
        }
    }
    if (requiresAuth && !loggedIn) {
        return next({ name: Routes.LOGIN });
    } else if (requiresAuth && loggedIn) {
        // refresh login on route changes, i.e., user action
        const login = await TokenService.refresh({
            refreshToken: TokenService.getRefreshToken(),
        });
        if (!login.success) {
            return next({ path: 'login', query: { session_timeout: true } });
        }

        const parentOrganization = TokenService.getParentOrganization();
        const isUser = TokenService.isUser();
        const isSuperUser = TokenService.isSuperUser();

        const now = new Date().getTime();
        const expiresAt = TokenService.getExpiresAt() || 0;
        if (now > expiresAt) {
            // clear storage of old token data
            TokenService.logout();
            return next({ path: 'login', query: { session_timeout: true } });
        }

        if (!isUser && !isSuperUser && !isBeesiteUser) {
            return next({ name: Routes.LOGIN });
        }

        const parentOrgState = store.state.Criteria['ParentOrganization'];
        if (isUser && parentOrgState !== parentOrganization) {
            let payload = {
                name: 'ParentOrganization',
                value: [parentOrganization],
            };
            store.commit(Criteria.mutations.SET_VALUE, payload);
        } else if (isSuperUser && parentOrgState) {
            let payload = {
                name: 'ParentOrganization',
                value: undefined,
            };
            store.commit(Criteria.mutations.SET_VALUE, payload);
        }
    }
    next(); // no auth needed or logged in as super user
});

const pushPullAnalysisService = new PushPullAnalysisService(config.jobAd);
const aiWriterService = new AiWriterService(config.aiWriterOptimize);
tinymcePlugin(pushPullAnalysisService, aiWriterService, i18n);

TokenService.registerRefreshInterceptor({
    handler: () => {
        const isOnLoginPage = router.currentRoute.name === Routes.LOGIN;

        if (!isOnLoginPage) {
            router.push({ name: Routes.LOGIN });
        }
    },
});

let VueApp = Vue.extend({
    methods: {
        JobAdSuggestionsService: () => new JobAdSuggestionsService(config),
    },
});

new VueApp({
    store,
    i18n,
    vuetify,
    router,
    render: (h) => h(App),
}).$mount('#app');
